/**
 * Cauldron registry — the top-level "environments" the portal is
 * organised into.
 *
 * - **Forward** — forward-market dashboards (electricity / gas / oil /
 *   coal / emissions / TB-spreads).
 * - **Flex** — flexibility-market dashboards (day-ahead / intraday /
 *   imbalance / aFRR / mFRR / FCR / capture price / cross-border).
 * - **Executive** — computational tooling (forecasts, benchmarks,
 *   simulators, orderbook).
 * - **API** — the API store (buy dataset API access), plus the user's API access,
 *   keys, usage and the API documentation.
 * - **Personalisation** — user-owned objects (Mijn omgeving + alerts).
 * - **Favourites** — the user's starred surfaces.
 * - **Platform** — settings / account / subscription surfaces, anchored
 *   to the rail's foot.
 *
 * News is intentionally NOT a cauldron — it sits as a standalone rail
 * entry above the cauldron group (next to Home).
 *
 * Each entry is consumed by:
 *   - `useActiveCauldron()` to detect which cauldron the current route belongs to
 *   - `CauldronRail.vue` to render the always-visible left rail
 *   - The `<{Id}Sidebar>.vue` components for their navigation contents
 *   - `StandardToolPage.vue` to fall back the eyebrow to the cauldron label
 *
 * Centralising the registry here keeps the source-of-truth small. Routes
 * carry `meta.cauldron` matching one of these ids; if a route hasn't been
 * tagged yet, `routePrefixes` provides a fallback so the rail still lights
 * up correctly.
 */
import {
  mdiAccountCircleOutline,
  mdiChartLine,
  mdiCogOutline,
  mdiLightningBolt,
  mdiStarOutline,
  mdiStorefrontOutline,
  mdiToolboxOutline,
} from '@mdi/js';

export const CAULDRON_IDS = {
  FORWARD: 'forward',
  FLEX: 'flex',
  EXECUTIVE: 'executive',
  API: 'api',
  PERSONALISATION: 'personalisation',
  FAVOURITES: 'favourites',
  PLATFORM: 'platform',
};

// Cauldron order = rail order, top → bottom. `placement: 'bottom'` flags
// the system cauldron so the shell can push it to the rail's bottom edge
// (Platform-style settings convention — same as VSCode/Notion).
export const CAULDRONS = [
  {
    id: CAULDRON_IDS.FORWARD,
    labelKey: 'cauldron.forward.label',
    tooltipKey: 'cauldron.forward.tooltip',
    icon: mdiChartLine,
    defaultPath: '/reports/forward',
    // Used by `useActiveCauldron` to detect active state from `route.path`
    // when a route has no explicit `meta.cauldron` tag yet.
    routePrefixes: ['/reports/forward'],
  },
  {
    id: CAULDRON_IDS.FLEX,
    labelKey: 'cauldron.flex.label',
    tooltipKey: 'cauldron.flex.tooltip',
    icon: mdiLightningBolt,
    defaultPath: '/reports/flexibility',
    routePrefixes: ['/reports/flexibility'],
  },
  {
    id: CAULDRON_IDS.EXECUTIVE,
    labelKey: 'cauldron.executive.label',
    tooltipKey: 'cauldron.executive.tooltip',
    icon: mdiToolboxOutline,
    defaultPath: '/tools',
    routePrefixes: ['/tools'],
  },
  {
    // The store gets its own rail entry so customers find it; it used to sit
    // under Platform > Developers. `/api` also covers `/api-docs*`.
    id: CAULDRON_IDS.API,
    labelKey: 'cauldron.api.label',
    tooltipKey: 'cauldron.api.tooltip',
    icon: mdiStorefrontOutline,
    defaultPath: '/api/store',
    routePrefixes: ['/api', '/account/api-keys'],
  },
  {
    id: CAULDRON_IDS.PERSONALISATION,
    labelKey: 'cauldron.personalisation.label',
    tooltipKey: 'cauldron.personalisation.tooltip',
    icon: mdiAccountCircleOutline,
    defaultPath: '/my-environment/overview',
    routePrefixes: [
      '/my-environment',
      '/mijn-omgeving',
      '/alerts',
      // Dashboard builder + viewer pages live under Personalisation:
      // they create and render user-owned content. `/my-dashboards` is
      // the legacy library URL (now a redirect to `/my-environment/
      // dashboards`); the rest are sub-pages the user navigates into
      // while building or viewing their dashboards.
      '/my-dashboards',
      '/builder',
      '/dashboards',
    ],
  },
  {
    id: CAULDRON_IDS.FAVOURITES,
    labelKey: 'cauldron.favourites.label',
    tooltipKey: 'cauldron.favourites.tooltip',
    icon: mdiStarOutline,
    // Favourites doesn't have its own route — the rail icon opens the
    // sidebar; clicking inside navigates to the favourited target. We
    // keep `defaultPath` as `/favorites` so the rail link is always
    // valid; the page itself can be a simple "Manage favourites" view
    // that lists all favourites (or we render an empty-state placeholder
    // when the route doesn't exist yet).
    defaultPath: '/favorites',
    routePrefixes: ['/favorites'],
  },
  {
    id: CAULDRON_IDS.PLATFORM,
    labelKey: 'cauldron.platform.label',
    tooltipKey: 'cauldron.platform.tooltip',
    icon: mdiCogOutline,
    defaultPath: '/profile',
    routePrefixes: [
      '/profile',
      '/my-subscription',
      '/platform/subscription',
      '/account/feedback',
      '/tenants',
      '/users',
      '/company',
    ],
    placement: 'bottom',
  },
];

const CAULDRONS_BY_ID = Object.fromEntries(CAULDRONS.map((c) => [c.id, c]));

/** Look up a cauldron by its id. Returns `null` for unknown ids. */
export function getCauldronById(id) {
  if (!id) return null;
  return CAULDRONS_BY_ID[id] || null;
}

/**
 * Resolve the cauldron for a given route path by matching its `routePrefixes`.
 * Returns `null` when no prefix matches — typically Home, Account, Developers,
 * or auth pages.
 */
export function getCauldronByPath(path) {
  if (!path) return null;
  for (const cauldron of CAULDRONS) {
    if (cauldron.routePrefixes.some((prefix) => path.startsWith(prefix))) {
      return cauldron;
    }
  }
  return null;
}
